import { userAuthState } from '@/@core/auth'
import useToast from '@/composables/useToast'
import { router } from '@/plugins/2.router'
import { ofetch } from 'ofetch'

const handleUnauthorizedResponse = () => {
  userAuthState().setToken(null)
  userAuthState().setUser(null)
  localStorage.removeItem('accessToken')

  // Avoid adding a redirect parameter if we're already heading to /login
  if (router.currentRoute.value.name !== 'login') {
    const redirectPath = router.currentRoute.value.fullPath

    router.push({ name: 'app-login', query: { to: redirectPath === '/app/login' ? undefined : redirectPath } })
  }
}

export const $api = ofetch.create({
  baseURL: '/api',
  async onRequest({ options }) {
    // Dynamic base URL from tenant store
    const { useTenantStore } = await import('@/stores/tenant')
    const tenantStore = useTenantStore()
    if (tenantStore.apiUrl) {
      options.baseURL = tenantStore.apiUrl
    }

    options.headers = {
      Accept: 'application/json',
      ...options.headers,
    }
    if (!options.headers['ContentType']) {
      options.headers['ContentType'] = 'application/json'
    }
    const accessToken = userAuthState().token ?? localStorage.getItem('accessToken')
    if (accessToken) {
      options.headers = {
        ...options.headers,
        
        Authorization: `Bearer ${accessToken}`,
      }
    }
  },
  onResponse({ response, error }) {
    if (response.status === 200|| response.status === 201|| response.status === 204) {
      // useToast().success('Success', 'success')
    } else if (response.status === 401) {
      // Don't handle 401 in onResponse for 2FA flows, let components handle it
      const currentRoute = router.currentRoute.value.name
      const auth = userAuthState()
      const isIn2FAFlow = currentRoute === 'app-two-step' || auth.tempToken

      if (!isIn2FAFlow) {
        handleUnauthorizedResponse()
      }
    } else if (response.status === 403) {
      useToast().error('You are not authorized to access this resource', 'error')
      response._toastShown = true
    } else if (response.status === 404) {
      // redirect to login page
      useToast().error('Not Found', 'error')
      response._toastShown = true
    } else if (response.status === 500) {
      // redirect to login page
      useToast().error('Server Error', 'error')
      response._toastShown = true
    }
    else if (response.status === 422) {
      if (Array.isArray(response?._data?.message) && response?._data?.message?.length > 0) {
        return
      }
      const isIn2FAFlow = router.currentRoute.value.name === 'app-two-step' || userAuthState().tempToken
      if (!isIn2FAFlow) {
        useToast().error(response?._data?.message || 'Unprocessable Entity', 'error')
        response._toastShown = true
      }
    }

    return response
  },
  onRequestError({ error }) {
    // if error code is 401, redirect to login page
    if (error.status === 401) {
      const currentRoute = router.currentRoute.value.name
      const auth = userAuthState()

      // Don't redirect if we're in 2FA flow (two-step page) or during login-2fa API calls
      const isIn2FAFlow = currentRoute === 'app-two-step' || auth.tempToken

      if (!isIn2FAFlow) {
        useToast().error('Unauthenticated', 'error')
        auth.setToken(null)
        auth.setUser(null)
        if (currentRoute !== 'login'){
          router.push({ name: 'app-login' })
        }
      }

      // For 2FA flows, let the component handle the error
    } else if (error.status === 403) {
      useToast().error('You are not authorized to access this resource', 'error')
      router.push({ name: 'not-authorized' }) // use the router instance here
    }

    // Don't show toast for 2FA verification errors as they're handled by components
    const isIn2FAFlow = router.currentRoute.value.name === 'app-two-step' || userAuthState().tempToken
    if (!isIn2FAFlow || error.status !== 401) {
      useToast().error(error, 'error')
    }
  },
  onResponseError({ error, response }) {
    // Create a new error object with response data attached
    const enhancedError = new Error(response._data?.message || error.message || 'An error occurred')

    enhancedError.status = response.status
    enhancedError.data = response._data
    enhancedError.response = response

    // onResponse always runs before onResponseError, so by now the global
    // handler above has decided whether to raise a toast for this response.
    // A caller with its own catch block reads this flag to avoid showing a
    // second toast saying the same thing — for a 422 the two messages are
    // character-for-character identical. It stays false wherever the global
    // handler stayed silent (401, a 2FA flow, an array-valued 422 message), so
    // a caller suppressing on it never swallows the only message the user gets.
    enhancedError.reported = response._toastShown === true

    throw enhancedError
  },
})
